import { MouseEvent as ReactMouseEvent, TouchEvent as ReactTouchEvent, useCallback, useEffect, useMemo, useState } from "react";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { TranslationKey } from "../../../shared/i18n/i18n";
import { isUuid } from "../../../shared/utils/uuid";
import { useAdminOrganizationContextCache } from "./AdminOrganizationContextCache";
import { AdminLeftPane } from "./AdminLeftPane";
import { AdminRightPane } from "./AdminRightPane";
import { AdminNavContext, AdminNavSelection } from "./AdminNavList";

type AdminWorkspaceProps = {
  currentUserSuperAdmin: boolean;
  isDesktopNavHidden: boolean;
  isLeftPaneCollapsed: boolean;
  isMobileNavOpen: boolean;
  isMobileViewport: boolean;
  leftPaneWidth: number;
  navigatorReloadKey: number;
  onCloseDesktopNav: () => void;
  onCloseMobileNav: () => void;
  onOpenDesktopNav: () => void;
  onStartLeftPaneResize: (event: ReactMouseEvent<HTMLButtonElement> | ReactTouchEvent<HTMLButtonElement>) => void;
  onToggleLeftPane: () => void;
  t: (key: TranslationKey) => string;
};

const DEFAULT_CONTEXT: AdminNavContext = {
  organizationId: null,
  level: "organizations",
  signId: null,
  siteId: null,
};

const isSameContext = (left: AdminNavContext, right: AdminNavContext) => (
  left.level === right.level
  && left.organizationId === right.organizationId
  && left.siteId === right.siteId
  && left.signId === right.signId
);

const isSameSelection = (left: AdminNavSelection | null, right: AdminNavSelection | null) => {
  if (!left && !right) {
    return true;
  }
  if (!left || !right) {
    return false;
  }
  return left.type === right.type
    && left.organizationId === right.organizationId
    && left.organizationTab === right.organizationTab
    && left.create === right.create
    && left.signTab === right.signTab
    && left.siteId === right.siteId
    && left.siteTab === right.siteTab
    && left.signId === right.signId
    && left.translationTab === right.translationTab
    && left.translationId === right.translationId;
};

const isValidId = (value: null | string | undefined): value is string => (
  isUuid(value)
);

const buildPathForContext = (context: AdminNavContext) => {
  if (context.level === "sites" && context.organizationId) {
    return `/a/organization/${context.organizationId}/sites`;
  }
  if (context.level === "signs" && context.organizationId && context.siteId) {
    return `/a/organization/${context.organizationId}/site/${context.siteId}/signs`;
  }
  if (context.level === "translations" && context.organizationId && context.siteId && context.signId) {
    return `/a/organization/${context.organizationId}/site/${context.siteId}/sign/${context.signId}/translations`;
  }
  return "/a/organizations";
};

const resolveContextFromPath = (pathname: string): AdminNavContext => {
  const translationCreateMatch = matchPath(
    { path: "/a/organization/:organizationId/site/:siteId/sign/:signId/translations/create" },
    pathname
  );
  if (translationCreateMatch?.params) {
    const organizationId = translationCreateMatch.params.organizationId;
    const siteId = translationCreateMatch.params.siteId;
    const signId = translationCreateMatch.params.signId;
    if (isValidId(organizationId) && isValidId(siteId) && isValidId(signId)) {
      return { organizationId, level: "translations", signId, siteId };
    }
  }

  const translationsMatch = matchPath(
    { path: "/a/organization/:organizationId/site/:siteId/sign/:signId/translations" },
    pathname
  );
  if (translationsMatch?.params) {
    const organizationId = translationsMatch.params.organizationId;
    const siteId = translationsMatch.params.siteId;
    const signId = translationsMatch.params.signId;
    if (isValidId(organizationId) && isValidId(siteId) && isValidId(signId)) {
      return { organizationId, level: "translations", signId, siteId };
    }
  }

  const signCreateMatch = matchPath({ path: "/a/organization/:organizationId/site/:siteId/signs/create" }, pathname);
  if (signCreateMatch?.params) {
    const organizationId = signCreateMatch.params.organizationId;
    const siteId = signCreateMatch.params.siteId;
    if (isValidId(organizationId) && isValidId(siteId)) {
      return { organizationId, level: "signs", signId: null, siteId };
    }
  }

  const signsMatch = matchPath({ path: "/a/organization/:organizationId/site/:siteId/signs" }, pathname);
  if (signsMatch?.params) {
    const organizationId = signsMatch.params.organizationId;
    const siteId = signsMatch.params.siteId;
    if (isValidId(organizationId) && isValidId(siteId)) {
      return { organizationId, level: "signs", signId: null, siteId };
    }
  }

  const siteCreateMatch = matchPath({ path: "/a/organization/:organizationId/sites/create" }, pathname);
  if (siteCreateMatch?.params) {
    const organizationId = siteCreateMatch.params.organizationId;
    if (isValidId(organizationId)) {
      return { organizationId, level: "sites", signId: null, siteId: null };
    }
  }

  const sitesMatch = matchPath({ path: "/a/organization/:organizationId/sites" }, pathname);
  if (sitesMatch?.params) {
    const organizationId = sitesMatch.params.organizationId;
    if (isValidId(organizationId)) {
      return { organizationId, level: "sites", signId: null, siteId: null };
    }
  }

  const translationDetailMatch = matchPath(
    { path: "/a/organization/:organizationId/site/:siteId/sign/:signId/translation/:translationId" },
    pathname
  );
  if (translationDetailMatch?.params) {
    const organizationId = translationDetailMatch.params.organizationId;
    const siteId = translationDetailMatch.params.siteId;
    const signId = translationDetailMatch.params.signId;
    if (isValidId(organizationId) && isValidId(siteId) && isValidId(signId)) {
      return { organizationId, level: "translations", signId, siteId };
    }
  }

  const translationTabMatch = matchPath(
    { path: "/a/organization/:organizationId/site/:siteId/sign/:signId/translation/:translationId/:translationTab" },
    pathname
  );
  if (translationTabMatch?.params) {
    const organizationId = translationTabMatch.params.organizationId;
    const siteId = translationTabMatch.params.siteId;
    const signId = translationTabMatch.params.signId;
    const translationId = translationTabMatch.params.translationId;
    const translationTab = translationTabMatch.params.translationTab;
    if ([organizationId, siteId, signId, translationId].every(isValidId) && translationTab === "details") {
      return { organizationId, level: "translations", signId, siteId };
    }
  }

  const signDetailMatch = matchPath({ path: "/a/organization/:organizationId/site/:siteId/sign/:signId" }, pathname);
  if (signDetailMatch?.params) {
    const organizationId = signDetailMatch.params.organizationId;
    const siteId = signDetailMatch.params.siteId;
    const signId = signDetailMatch.params.signId;
    if (isValidId(organizationId) && isValidId(siteId) && isValidId(signId)) {
      return { organizationId, level: "signs", signId, siteId };
    }
  }

  const signTabMatch = matchPath({ path: "/a/organization/:organizationId/site/:siteId/sign/:signId/:signTab" }, pathname);
  if (signTabMatch?.params) {
    const organizationId = signTabMatch.params.organizationId;
    const siteId = signTabMatch.params.siteId;
    const signId = signTabMatch.params.signId;
    if (isValidId(organizationId) && isValidId(siteId) && isValidId(signId)) {
      return { organizationId, level: "signs", signId, siteId };
    }
  }

  const siteDetailMatch = matchPath({ path: "/a/organization/:organizationId/site/:siteId" }, pathname);
  if (siteDetailMatch?.params) {
    const organizationId = siteDetailMatch.params.organizationId;
    const siteId = siteDetailMatch.params.siteId;
    if (isValidId(organizationId) && isValidId(siteId)) {
      return { organizationId, level: "sites", signId: null, siteId };
    }
  }

  const siteTabMatch = matchPath({ path: "/a/organization/:organizationId/site/:siteId/:siteTab" }, pathname);
  if (siteTabMatch?.params) {
    const organizationId = siteTabMatch.params.organizationId;
    const siteId = siteTabMatch.params.siteId;
    if (isValidId(organizationId) && isValidId(siteId)) {
      return { organizationId, level: "sites", signId: null, siteId };
    }
  }

  const organizationTabMatch = matchPath({ path: "/a/organization/:organizationId/:organizationTab" }, pathname);
  if (organizationTabMatch?.params) {
    const organizationId = organizationTabMatch.params.organizationId;
    if (isValidId(organizationId)) {
      return { organizationId: null, level: "organizations", signId: null, siteId: null };
    }
  }

  const organizationDetailMatch = matchPath({ path: "/a/organization/:organizationId" }, pathname);
  if (organizationDetailMatch?.params) {
    const organizationId = organizationDetailMatch.params.organizationId;
    if (isValidId(organizationId)) {
      return { organizationId: null, level: "organizations", signId: null, siteId: null };
    }
  }

  const organizationCreateMatch = matchPath({ path: "/a/organizations/create" }, pathname);
  if (organizationCreateMatch) {
    return DEFAULT_CONTEXT;
  }

  const organizationsMatch = matchPath({ path: "/a/organizations" }, pathname);
  if (organizationsMatch) {
    return DEFAULT_CONTEXT;
  }

  return DEFAULT_CONTEXT;
};

const resolveSelectionFromPath = (pathname: string): AdminNavSelection | null => {
  const translationCreateMatch = matchPath(
    { path: "/a/organization/:organizationId/site/:siteId/sign/:signId/translations/create" },
    pathname
  );
  if (translationCreateMatch?.params) {
    const organizationId = translationCreateMatch.params.organizationId;
    const siteId = translationCreateMatch.params.siteId;
    const signId = translationCreateMatch.params.signId;
    if ([organizationId, siteId, signId].every(isValidId)) {
      return { organizationId, create: true, signId, siteId, type: "translation" };
    }
  }

  const translationMatch = matchPath(
    { path: "/a/organization/:organizationId/site/:siteId/sign/:signId/translation/:translationId" },
    pathname
  );
  if (translationMatch?.params) {
    const organizationId = translationMatch.params.organizationId;
    const siteId = translationMatch.params.siteId;
    const signId = translationMatch.params.signId;
    const translationId = translationMatch.params.translationId;
    if ([organizationId, siteId, signId, translationId].every(isValidId)) {
      return { organizationId, signId, siteId, translationId, type: "translation" };
    }
  }

  const translationTabMatch = matchPath(
    { path: "/a/organization/:organizationId/site/:siteId/sign/:signId/translation/:translationId/:translationTab" },
    pathname
  );
  if (translationTabMatch?.params) {
    const organizationId = translationTabMatch.params.organizationId;
    const siteId = translationTabMatch.params.siteId;
    const signId = translationTabMatch.params.signId;
    const translationId = translationTabMatch.params.translationId;
    const translationTab = translationTabMatch.params.translationTab;
    if ([organizationId, siteId, signId, translationId].every(isValidId) && translationTab === "details") {
      return { organizationId, signId, siteId, translationId, translationTab, type: "translation" };
    }
  }

  const signCreateMatch = matchPath({ path: "/a/organization/:organizationId/site/:siteId/signs/create" }, pathname);
  if (signCreateMatch?.params) {
    const organizationId = signCreateMatch.params.organizationId;
    const siteId = signCreateMatch.params.siteId;
    if ([organizationId, siteId].every(isValidId)) {
      return { organizationId, create: true, siteId, type: "sign" };
    }
  }

  const signTabMatch = matchPath({ path: "/a/organization/:organizationId/site/:siteId/sign/:signId/:signTab" }, pathname);
  if (signTabMatch?.params) {
    const organizationId = signTabMatch.params.organizationId;
    const siteId = signTabMatch.params.siteId;
    const signId = signTabMatch.params.signId;
    const signTab = signTabMatch.params.signTab;
    if ([organizationId, siteId, signId].every(isValidId) && (signTab === "details" || signTab === "urls")) {
      return { organizationId, signId, signTab, siteId, type: "sign" };
    }
  }

  const signMatch = matchPath({ path: "/a/organization/:organizationId/site/:siteId/sign/:signId" }, pathname);
  if (signMatch?.params) {
    const organizationId = signMatch.params.organizationId;
    const siteId = signMatch.params.siteId;
    const signId = signMatch.params.signId;
    if ([organizationId, siteId, signId].every(isValidId)) {
      return { organizationId, signId, signTab: "details", siteId, type: "sign" };
    }
  }

  const siteTabMatch = matchPath({ path: "/a/organization/:organizationId/site/:siteId/:siteTab" }, pathname);
  if (siteTabMatch?.params) {
    const organizationId = siteTabMatch.params.organizationId;
    const siteId = siteTabMatch.params.siteId;
    const siteTab = siteTabMatch.params.siteTab;
    if ([organizationId, siteId].every(isValidId) && siteTab === "details") {
      return { organizationId, siteId, siteTab, type: "site" };
    }
  }

  const siteCreateMatch = matchPath({ path: "/a/organization/:organizationId/sites/create" }, pathname);
  if (siteCreateMatch?.params) {
    const organizationId = siteCreateMatch.params.organizationId;
    if (isValidId(organizationId)) {
      return { organizationId, create: true, type: "site" };
    }
  }

  const siteMatch = matchPath({ path: "/a/organization/:organizationId/site/:siteId" }, pathname);
  if (siteMatch?.params) {
    const organizationId = siteMatch.params.organizationId;
    const siteId = siteMatch.params.siteId;
    if ([organizationId, siteId].every(isValidId)) {
      return { organizationId, siteId, siteTab: "details", type: "site" };
    }
  }

  const organizationCreateMatch = matchPath({ path: "/a/organizations/create" }, pathname);
  if (organizationCreateMatch) {
    return { create: true, type: "organization" };
  }

  const organizationTabMatch = matchPath({ path: "/a/organization/:organizationId/:organizationTab" }, pathname);
  if (organizationTabMatch?.params) {
    const organizationId = organizationTabMatch.params.organizationId;
    const organizationTab = organizationTabMatch.params.organizationTab;
    if (isValidId(organizationId) && (organizationTab === "details" || organizationTab === "users" || organizationTab === "subscription" || organizationTab === "domains" || organizationTab === "internals")) {
      return { organizationId, organizationTab, type: "organization" };
    }
  }

  const organizationMatch = matchPath({ path: "/a/organization/:organizationId" }, pathname);
  if (organizationMatch?.params) {
    const organizationId = organizationMatch.params.organizationId;
    if (isValidId(organizationId)) {
      return { organizationId, organizationTab: "details", type: "organization" };
    }
  }

  return null;
};

const buildPathForSelection = (selection: AdminNavSelection): string => {
  if (selection.create) {
    if (selection.type === "organization") {
      return "/a/organizations/create";
    }
    if (selection.type === "site") {
      return `/a/organization/${selection.organizationId}/sites/create`;
    }
    if (selection.type === "sign") {
      return `/a/organization/${selection.organizationId}/site/${selection.siteId}/signs/create`;
    }
    return `/a/organization/${selection.organizationId}/site/${selection.siteId}/sign/${selection.signId}/translations/create`;
  }
  if (selection.type === "organization") {
    return `/a/organization/${selection.organizationId}/${selection.organizationTab ?? "details"}`;
  }
  if (selection.type === "site") {
    return `/a/organization/${selection.organizationId}/site/${selection.siteId}/${selection.siteTab ?? "details"}`;
  }
  if (selection.type === "sign") {
    return `/a/organization/${selection.organizationId}/site/${selection.siteId}/sign/${selection.signId}/${selection.signTab ?? "details"}`;
  }
  return `/a/organization/${selection.organizationId}/site/${selection.siteId}/sign/${selection.signId}/translation/${selection.translationId}/${selection.translationTab ?? "details"}`;
};

export const AdminWorkspace = ({
  currentUserSuperAdmin,
  isDesktopNavHidden,
  isLeftPaneCollapsed,
  isMobileNavOpen,
  isMobileViewport,
  leftPaneWidth,
  navigatorReloadKey,
  onCloseDesktopNav,
  onCloseMobileNav,
  onOpenDesktopNav,
  onStartLeftPaneResize,
  onToggleLeftPane,
  t,
}: AdminWorkspaceProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { ensureOrganizationContext } = useAdminOrganizationContextCache();

  const [navContext, setNavContext] = useState<AdminNavContext>(DEFAULT_CONTEXT);
  const [localNavReloadKey, setLocalNavReloadKey] = useState(0);
  const [selection, setSelection] = useState<AdminNavSelection | null>(null);

  const resolvedContext = useMemo(() => resolveContextFromPath(location.pathname), [location.pathname]);
  const resolvedSelection = useMemo(() => resolveSelectionFromPath(location.pathname), [location.pathname]);

  useEffect(() => {
    setNavContext((prev) => (isSameContext(prev, resolvedContext) ? prev : resolvedContext));
    setSelection((prev) => (isSameSelection(prev, resolvedSelection) ? prev : resolvedSelection));
  }, [resolvedContext, resolvedSelection]);

  useEffect(() => {
    const activeOrganizationId = resolvedSelection?.organizationId ?? resolvedContext.organizationId;
    if (!activeOrganizationId) {
      return;
    }
    void ensureOrganizationContext(activeOrganizationId);
  }, [ensureOrganizationContext, resolvedContext.organizationId, resolvedSelection?.organizationId]);

  const handleContextChange = useCallback(
    (nextContext: AdminNavContext) => {
      if (isSameContext(nextContext, navContext)) {
        return;
      }
      setNavContext(nextContext);
      setSelection(null);
      const nextPath = buildPathForContext(nextContext);
      if (nextPath !== location.pathname) {
        navigate(nextPath);
      }
    },
    [location.pathname, navContext, navigate]
  );

  const handleSelectionChange = useCallback(
    (nextSelection: AdminNavSelection | null) => {
      if (isSameSelection(nextSelection, selection)) {
        return;
      }
      setSelection(nextSelection);
      if (isMobileNavOpen) {
        onCloseMobileNav();
      }
      if (!nextSelection) {
        const nextPath = buildPathForContext(navContext);
        if (nextPath !== location.pathname) {
          navigate(nextPath);
        }
        return;
      }
      const nextPath = buildPathForSelection(nextSelection);
      if (nextPath !== location.pathname) {
        navigate(nextPath);
      }
    },
    [isMobileNavOpen, location.pathname, navigate, navContext, onCloseMobileNav, selection]
  );

  const handleEntityMutated = useCallback(() => {
    setLocalNavReloadKey((current) => current + 1);
  }, []);

  return (
    <>
      <AdminLeftPane
        contextOverride={navContext}
        isCollapsed={isLeftPaneCollapsed}
        isDesktopHidden={isDesktopNavHidden}
        isMobileNavOpen={isMobileNavOpen}
        isMobileViewport={isMobileViewport}
        onCloseMobileNav={onCloseMobileNav}
        onContextChange={handleContextChange}
        onSelectEntity={handleSelectionChange}
        onStartResize={onStartLeftPaneResize}
        onToggleCollapse={onToggleLeftPane}
        reloadKey={localNavReloadKey + navigatorReloadKey}
        selectedEntity={selection}
        t={t}
        width={leftPaneWidth}
      />
      <AdminRightPane
        context={navContext}
        currentUserSuperAdmin={currentUserSuperAdmin}
        isDesktopNavHidden={isDesktopNavHidden}
        isMobileViewport={isMobileViewport}
        onCloseDesktopNav={onCloseDesktopNav}
        onEntityMutated={handleEntityMutated}
        onOpenDesktopNav={onOpenDesktopNav}
        onSelectionChange={handleSelectionChange}
        selection={selection}
        t={t}
      />
    </>
  );
};
